import { FunctionComponent, useEffect, useState } from 'react';
import { Feature, GeoJsonProperties, Geometry } from 'geojson';

import MapaImpactoVisor, {
  EscenarioMapa,
  ImpactoMapaPayload
} from './MapaImpactoVisor';
import { SimpleObject } from '../../types';
import { getUncatchEndpointData } from '../../utils/request';

export type { EscenarioMapa } from './MapaImpactoVisor';

type ImpactoMapaResponse = {
  snapshot_dmin?: ImpactoMapaPayload;
  snapshot_dmax?: ImpactoMapaPayload;
  dia_verano?: ImpactoMapaPayload;
  dia_invierno?: ImpactoMapaPayload;
} | null;

type Props = {
  estudioId: string | number;
  /** Escenario a visualizar en el mapa. Independiente del selector de
   *  "Análisis gráfico por escenario" (boxplots): este componente vive en su
   *  propio bloque con selector dedicado de 4 opciones. */
  escenarioMapa: EscenarioMapa;
  /** Estado del mapa (features ya cargadas) compartido con el bloque
   *  "Ubicación del Proyecto"; reutilizamos para no duplicar fetch. */
  mapBaseState: {
    mapPointFeatures: Feature<Geometry, GeoJsonProperties>[];
    mapLineFeatures: Feature<Geometry, GeoJsonProperties>[];
    mapPolygonFeatures: Feature<Geometry, GeoJsonProperties>[];
    electricFeeders: SimpleObject;
    mapCenter: { lat: number; lng: number };
  };
};

const ETIQUETA_ESCENARIO: Record<EscenarioMapa, string> = {
  snapshot_dmin: 'Snapshot Dmin',
  snapshot_dmax: 'Snapshot Dmax',
  dia_verano: 'Día Verano',
  dia_invierno: 'Día Invierno'
};

/** Bloque de mapa de impacto para Fase 1 (caso con proyecto vs caso sin
 *  proyecto). Hace fetch a `/impacto-mapa` y delega el rendering al visor
 *  presentacional `MapaImpactoVisor`. Para Fase 2 (mitigaciones) ver
 *  `MapaImpactoMitigacion.tsx`. */
const MapaImpacto: FunctionComponent<Props> = ({
  estudioId,
  escenarioMapa,
  mapBaseState
}) => {
  const [impacto, setImpacto] = useState<ImpactoMapaResponse>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchImpacto = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getUncatchEndpointData({
          endpoint: `estudios-conexion/${estudioId}/impacto-mapa`
        });
        if (!cancelled) {
          setImpacto(data || null);
        }
      } catch (err) {
        if (!cancelled) {
          setError('No se pudo cargar el mapa de impacto.');
          setImpacto(null);
        }
      }
      if (!cancelled) setLoading(false);
    };
    fetchImpacto();
    return () => {
      cancelled = true;
    };
  }, [estudioId]);

  const payload = impacto ? impacto[escenarioMapa] ?? null : null;

  const hint = (
    <>
      El mapa resalta los elementos cuyo problema es <strong>causado por el proyecto</strong>{' '}
      (existe con el proyecto y no existía sin él), comparando dentro del escenario{' '}
      <strong>{ETIQUETA_ESCENARIO[escenarioMapa]}</strong>. Seleccione qué capa visualizar.
    </>
  );

  return (
    <MapaImpactoVisor
      payload={payload}
      mapBaseState={mapBaseState}
      loading={loading}
      error={error}
      hint={hint}
    />
  );
};

export default MapaImpacto;
